import { Fragment, useMemo, useState } from 'react';
import {
  FileText,
  AlertCircle,
  CheckCircle2,
  Download,
  HelpCircle,
  MinusCircle,
  RotateCcw,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type { CsvParseError, MassMigrationOrgType, ParsedCsvResult } from '@/types/massMigration';
import {
  COLUMN_DISPLAY_NAMES,
  COLUMN_TO_ROW_PROP,
  buildColumnStatuses,
  columnLabel,
  getColumns,
} from './csvColumns';

/** Chip appearance per column match status — see buildColumnStatuses for why 'unknown' exists. */
const CHIP_STYLES = {
  matched: {
    icon: CheckCircle2,
    label: 'auto-matched',
    className: 'border-success/50 bg-success/10 text-success',
  },
  missing: {
    icon: AlertCircle,
    label: 'missing',
    className: 'border-destructive/50 bg-destructive/10 text-destructive',
  },
  absent: {
    icon: MinusCircle,
    label: 'not in file',
    className: 'border-border bg-muted text-muted-foreground',
  },
  unknown: {
    icon: HelpCircle,
    label: 'not detected',
    className: 'border-border bg-muted text-muted-foreground',
  },
} as const;

// ─── Download helpers ─────────────────────────────────────────────────────────

function downloadErrorReport(result: ParsedCsvResult): void {
  // Deduplicate by row number: group all errors per row into one line
  const rowErrorMap = result.errors.reduce<Record<number, string[]>>((acc, e) => {
    const existing = acc[e.rowNumber] ?? [];
    const label = columnLabel(e.column);
    existing.push(label !== null ? `${label}: ${e.message}` : e.message);
    acc[e.rowNumber] = existing;
    return acc;
  }, {});

  const header = 'rowNumber,errors\n';
  const rows = Object.entries(rowErrorMap)
    .map(([rowNum, msgs]) => `${rowNum},"${msgs.join(' | ')}"`)
    .join('\n');
  const csv = header + rows;
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'error-report.csv';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ─── UploadPreviewA — Spreadsheet ─────────────────────────────────────────────

export interface UploadPreviewAProps {
  result: ParsedCsvResult;
  fileName: string;
  orgType: MassMigrationOrgType;
  onReplace: () => void;
}

export function UploadPreviewA({ result, fileName, orgType, onReplace }: UploadPreviewAProps) {
  const [showErrorsOnly, setShowErrorsOnly] = useState(false);
  const hasErrors = result.errors.length > 0;

  // rowNumber 0 = file-level errors (missing required column, row cap exceeded);
  // they match no table row, so they render in the alert instead.
  const { fileErrors, rowErrors } = useMemo(
    () => ({
      fileErrors: result.errors.filter((e) => e.rowNumber === 0),
      rowErrors: result.errors.filter((e) => e.rowNumber > 0),
    }),
    [result.errors],
  );

  const errorRowNumbers = new Set(rowErrors.map((e) => e.rowNumber));

  const errorByRow = rowErrors.reduce<Record<number, CsvParseError[]>>((acc, e) => {
    const existing = acc[e.rowNumber] ?? [];
    existing.push(e);
    acc[e.rowNumber] = existing;
    return acc;
  }, {});

  const displayedRows = showErrorsOnly
    ? result.rows.filter((r) => errorRowNumbers.has(r.rowNumber))
    : result.rows;

  // Use orgType-aware column list — Fix 3
  const columnKeys = getColumns(orgType);

  const columnStatuses = useMemo(
    () => buildColumnStatuses(columnKeys, result, fileErrors),
    [columnKeys, result, fileErrors],
  );

  return (
    <div className="space-y-4">
      {/* File summary */}
      <div className="flex flex-wrap items-center gap-3 rounded-md border border-border bg-muted/30 px-4 py-2">
        <FileText className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
        <span className="text-sm font-medium text-foreground">{fileName}</span>
        <span className="text-xs text-muted-foreground">{result.totalRows} rows</span>
        {hasErrors && (
          <Badge variant="destructive">{result.errors.length} errors</Badge>
        )}
      </div>

      {/* Column mapping chips */}
      <div className="flex flex-wrap gap-2">
        {columnKeys.map((col) => {
          const status = columnStatuses[col] ?? 'unknown';
          const chip = CHIP_STYLES[status];
          const ChipIcon = chip.icon;
          return (
            <span
              key={col}
              className={cn(
                'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs',
                chip.className,
              )}
            >
              <ChipIcon className="h-3 w-3" aria-hidden="true" />
              {COLUMN_DISPLAY_NAMES[col]}
              <span className="ml-0.5 opacity-80">{chip.label}</span>
            </span>
          );
        })}
      </div>

      {/* Error alert */}
      {hasErrors && (
        <Alert variant="warning">
          <AlertCircle className="h-4 w-4" aria-hidden="true" />
          <AlertTitle>
            {result.errors.length} {result.errors.length === 1 ? 'error' : 'errors'} found
          </AlertTitle>
          <AlertDescription className="mt-2 space-y-2">
            {fileErrors.length > 0 && (
              <ul className="space-y-0.5" aria-label="File errors">
                {fileErrors.map((e) => {
                  const label = columnLabel(e.column);
                  return (
                    <li key={`${e.column}-${e.message}`} className="text-sm">
                      {label !== null && <span className="font-medium">{label}: </span>}
                      {e.message}
                    </li>
                  );
                })}
              </ul>
            )}
            <div className="flex items-center gap-3">
              <span className="text-sm">Fix errors before continuing.</span>
              {errorRowNumbers.size > 0 && (
                <button
                  type="button"
                  onClick={() => setShowErrorsOnly((v) => !v)}
                  className="text-xs underline"
                >
                  {showErrorsOnly ? 'Show all rows' : 'Show errors only'}
                </button>
              )}
            </div>
          </AlertDescription>
        </Alert>
      )}

      {/* Spreadsheet table */}
      <div className="max-h-[400px] overflow-y-auto rounded-md border border-border">
        <table className="w-full min-w-max text-xs">
          <thead className="sticky top-0 bg-muted">
            <tr>
              <th className="px-3 py-2 text-left text-muted-foreground font-medium whitespace-nowrap">CSV row</th>
              {columnKeys.map((col) => (
                <th key={col} className="px-3 py-2 text-left text-muted-foreground font-medium">
                  {COLUMN_DISPLAY_NAMES[col]}
                </th>
              ))}
              <th className="px-3 py-2 text-left text-muted-foreground font-medium">
                <span className="sr-only">Status</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {displayedRows.map((row) => {
              const errorsForRow = errorByRow[row.rowNumber] ?? [];
              const hasRowError = errorsForRow.length > 0;
              const errorColumns = new Set(errorsForRow.map((e) => e.column));
              return (
                <Fragment key={row.rowNumber}>
                  <tr
                    className={cn(
                      'border-t border-border',
                      hasRowError ? 'bg-destructive/5' : 'bg-background',
                    )}
                  >
                    <td className="px-3 py-1.5 text-muted-foreground">{row.rowNumber}</td>
                    {columnKeys.map((col) => {
                      const val = row[COLUMN_TO_ROW_PROP[col]];
                      const cellHasError = errorColumns.has(col);
                      return (
                        <td
                          key={col}
                          className={cn(
                            'px-3 py-1.5 text-foreground',
                            cellHasError &&
                              'bg-destructive/10 text-destructive font-medium',
                          )}
                        >
                          {val === null || val === undefined ? (
                            <span className="text-muted-foreground">—</span>
                          ) : (
                            String(val)
                          )}
                        </td>
                      );
                    })}
                    <td className="px-3 py-1.5">
                      {hasRowError && (
                        <AlertCircle
                          className="h-4 w-4 text-destructive"
                          aria-hidden="true"
                        />
                      )}
                    </td>
                  </tr>
                  {hasRowError && (
                    <tr className="bg-destructive/5">
                      <td />
                      <td colSpan={columnKeys.length + 1} className="px-3 pb-2 pt-0">
                        {/* sticky keeps the message visible while the table scrolls horizontally */}
                        <ul
                          className="sticky left-3 w-fit space-y-0.5 text-destructive"
                          aria-label={`Row ${row.rowNumber} errors`}
                        >
                          {errorsForRow.map((e) => {
                            const label = columnLabel(e.column);
                            return (
                              <li
                                key={`${e.column}-${e.message}`}
                                className="flex items-start gap-1.5"
                              >
                                <AlertCircle
                                  className="mt-0.5 h-3 w-3 shrink-0"
                                  aria-hidden="true"
                                />
                                <span>
                                  {label !== null && (
                                    <span className="font-medium">{label}: </span>
                                  )}
                                  {e.message}
                                </span>
                              </li>
                            );
                          })}
                        </ul>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-2">
        {hasErrors && (
          <Button
            variant="outline"
            size="sm"
            type="button"
            onClick={() => downloadErrorReport(result)}
          >
            <Download className="mr-2 h-4 w-4" aria-hidden="true" />
            Download error report
          </Button>
        )}
        <Button variant="outline" size="sm" type="button" onClick={onReplace}>
          <RotateCcw className="mr-2 h-4 w-4" aria-hidden="true" />
          Replace file
        </Button>
      </div>
    </div>
  );
}
